/**
 * useDeployments Hook
 * 
 * Fetches the current user's accessible VenueFlow deployments
 * from the vf_my_deployments view (respects RLS automatically).
 * 
 * Lumen8 staff see everything. Club users see their club.
 * Commercial partners see their granted deployments.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

export function useDeployments(user) {
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDeployments = useCallback(async () => {
    if (!user || !supabase) {
      setDeployments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from("vf_my_deployments")
        .select("*")
        .order("sort_order", { ascending: true });

      if (queryError) throw queryError;

      // Parse venues JSON if needed
      const parsed = (data || []).map((d) => ({
        ...d,
        venues: typeof d.venues === "string" ? JSON.parse(d.venues) : d.venues || [],
      }));

      setDeployments(parsed);
    } catch (err) {
      console.error("Failed to fetch deployments:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDeployments();
  }, [fetchDeployments]);

  return { deployments, loading, error, refetch: fetchDeployments };
}

export default useDeployments;
